import { Controller } from "@hotwired/stimulus"

// Reply (and Reply all, on email) live on the latest message card. Cards arrive
// later over the conversation stream, so this watches the timeline and keeps
// exactly one row visible — and only when this viewer may write to the client.
// The broadcast cannot know that: it is the same markup for every subscriber.
//
// A morph refresh (project watcher updates) puts the server's `hidden` back on
// every row without adding or removing a card, so the observer never fires.
// `turbo:morph@document` calls `refresh`, which re-binds to the live timeline
// and reconciles again.
export default class extends Controller {
  static values = { repliable: Boolean }

  connect() {
    this.reconcile = this.reconcile.bind(this)
    this.observer = new MutationObserver(this.reconcile)
    this.refresh()
  }

  disconnect() {
    this.observer?.disconnect()
    this.observed = null
  }

  refresh() {
    const messages = this.messages()
    if (messages !== this.observed) {
      this.observer.disconnect()
      if (messages) this.observer.observe(messages, { childList: true })
      this.observed = messages
    }
    this.reconcile()
  }

  repliableValueChanged() {
    if (this.observer) this.reconcile()
  }

  open(event) {
    event.preventDefault()
    const composer = this.composer()
    if (composer) {
      this.openComposer(composer, event.currentTarget)
      return
    }

    const root = this.element.querySelector("#reply_composer")
    if (!root) return

    root.hidden = false
    root.classList.add("is-open")
    root.querySelector(".composer__editor")?.focus()
    const scroller = root.closest(".thread__scroll")
    if (scroller) scroller.scrollTop = scroller.scrollHeight
  }

  // Latest-card Reply / Reply all carry the same prefills as the ⋯ menu. Slack
  // has neither attr, so it still just opens.
  openComposer(composer, button) {
    if (button?.hasAttribute("data-reply-all-to")) {
      composer.composeFromMessage({
        to: button.dataset.replyAllTo || "",
        cc: button.dataset.replyAllCc || "",
        parentMessageId: button.dataset.messageId || ""
      })
      return
    }

    if (button?.hasAttribute("data-reply-to")) {
      composer.composeFromMessage({
        to: button.dataset.replyTo || "",
        parentMessageId: button.dataset.messageId || ""
      })
      return
    }

    composer.open()
  }

  reconcile() {
    const cards = this.element.querySelectorAll(
      "#conversation_messages .message-card:not(.message-card--comment)"
    )
    const latest = cards[cards.length - 1]
    this.element.querySelectorAll(".message-reply").forEach((row) => {
      row.hidden = !this.repliableValue || !latest || !latest.contains(row)
    })
  }

  messages() {
    return this.element.querySelector("#conversation_messages")
  }

  composer() {
    const root = this.element.querySelector("#reply_composer")
    const form = root?.querySelector("[data-controller~='composer']") || root?.querySelector("form")
    if (!form) return null

    return this.application.getControllerForElementAndIdentifier(form, "composer")
  }
}
